import React, { useState, useRef } from "react";
import { Add } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { toast } from "react-toastify";

import FormDrawer from "components/Shared/FormDrawer";
import { useVehicleDetailContext } from "contexts/VehicleDetailContext";
import {
  useAddVehicleFuelMutation,
  useUpdateVehicleFuelMutation,
  useListVehicleFuelByVehicleIdQuery,
} from "store/services/vehicleFuelApi";
import VehicleFuelListByVehicle from "../../VehicleFuel/VehicleFuelComponents/VehicleFuelListByVehicle";
import VehicleFuelFormDrawer from "./VehicleFuelTabFromDrawer";
import VehicleMileageSummary from "./VehicleMileageSummary"; // Import the new component

const VehicleFuelTab = () => {
  const [showFuelForm, setShowFuelForm] = useState(false);
  const [selectedFuel, setSelectedFuel] = useState(null);
  const mileageSummaryRef = useRef(null);

  const { vehicleDetail } = useVehicleDetailContext();

  // RTK Query - Get the latest fuel record to extract previous odometer
  const { data: vehicleFuelRs } = useListVehicleFuelByVehicleIdQuery(
    {
      vehicleId: vehicleDetail?._id,
      page: 1,
      limit: 1,
    },
    {
      skip: !vehicleDetail?._id,
    }
  );

  const [addVehicleFuel, { isLoading: addingFuel }] =
    useAddVehicleFuelMutation();
  const [updateVehicleFuel, { isLoading: updatingFuel }] =
    useUpdateVehicleFuelMutation();

  // Get previous odometer from the latest fuel record
  const previousOdometer = React.useMemo(() => {
    if (vehicleFuelRs?.data && vehicleFuelRs.data.length > 0) {
      return vehicleFuelRs.data[0].odometer || 0;
    }
    return 0;
  }, [vehicleFuelRs]);

  // Methods
  const handleCancelFuel = () => {
    setShowFuelForm(false);
    setSelectedFuel(null);
  };

  const handleAddOrUpdateFuel = async (data) => {
    try {
      if (selectedFuel) {
        await updateVehicleFuel({
          vehicleId: vehicleDetail?._id,
          fuelId: selectedFuel._id,
          ...data,
        }).unwrap();
        toast.success("Fuel record updated successfully!");
      } else {
        await addVehicleFuel({
          vehicleId: vehicleDetail?._id,
          ...data,
        }).unwrap();
        toast.success("Fuel record added successfully!");
      }
      handleCancelFuel();

      // Refresh mileage data after adding/updating fuel record
      setTimeout(() => {
        if (
          mileageSummaryRef.current &&
          mileageSummaryRef.current.refreshMileageData
        ) {
          mileageSummaryRef.current.refreshMileageData();
        }
      }, 1000);
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't process fuel record!");
    }
  };

  return (
    <Box padding={3}>
      {/* Header with Add Button */}
      <VehicleMileageSummary
        vehicleId={vehicleDetail?._id}
        ref={mileageSummaryRef}
      />
      <Box display="flex" justifyContent="end" mb={3}>
        {!showFuelForm && (
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={() => setShowFuelForm(true)}
          >
            Add Fuel Record
          </Button>
        )}
      </Box>

      {/* Vehicle Mileage Summary Component */}

      {/* Vehicle Fuel List */}
      <VehicleFuelListByVehicle />

      {/* Form Drawer */}
      <FormDrawer
        title="Vehicle Fuel Record"
        open={showFuelForm}
        onClose={handleCancelFuel}
      >
        <VehicleFuelFormDrawer
          isBusy={addingFuel || updatingFuel}
          vehicleFuel={selectedFuel}
          previousOdometer={previousOdometer}
          onCancel={handleCancelFuel}
          onSubmit={handleAddOrUpdateFuel}
        />
      </FormDrawer>
    </Box>
  );
};

export default VehicleFuelTab;
